<template>
  <RouterLink
    v-if="tab.path"
    v-focus
    :to="tab.path"
    class="nav-bar__item"
    :class="{ active: isActive(tab) }"
  >
    <i :class="iconClass" aria-hidden="true" />
    <Transition name="fade">
      <span
        v-show="!isMenuCollapsed && isTransitionEnded"
        class="title-medium-regular nav-bar__item__text"
      >
        {{ displayName }}
      </span>
    </Transition>
  </RouterLink>

  <button
    v-else
    v-focus
    class="nav-bar__item__collapse"
    :class="{ active: isActive(tab) }"
    type="button"
    :aria-expanded="hasDropdown ? String(isDropdownOpen) : null"
    :aria-label="displayName"
    @click="handleClick"
  >
    <i :class="iconClass" aria-hidden="true" />
    <Transition name="fade">
      <span
        v-show="hasDropdown && !isMenuCollapsed && isTransitionEnded"
        class="title-medium-regular nav-bar__item__text"
      >
        {{ displayName }}
      </span>
    </Transition>
    <Transition name="fade">
      <i
        v-if="hasDropdown && !isMenuCollapsed && isTransitionEnded"
        :class="`fa fa-chevron-${isDropdownOpen ? 'up' : 'down'}`"
      />
    </Transition>
  </button>
</template>

<script setup>
import { useSideMenu } from "@follaw-fr/composables/useSideMenu";
import { computed } from "vue";

const props = defineProps({
  tab: { type: Object, required: true },
  activeDropdown: { type: String, default: null },
  isMenuCollapsed: { type: Boolean, default: false },
  isTransitionEnded: { type: Boolean, default: false },
});

const displayName = computed(() => {
  return props.tab.displayName || props.tab.name;
});

const { isActive } = useSideMenu();

const emit = defineEmits(["toggleDropdown"]);

const isDropdownOpen = computed(() => props.activeDropdown === props.tab.name);

const hasDropdown = computed(
  () => props.tab.secondary_tabs && props.tab.secondary_tabs.length > 0
);

const handleClick = () => {
  if (hasDropdown.value) {
    emit("toggleDropdown", props.tab.name);
  }
};

const iconClass = computed(() => {
  return isActive(props.tab)
    ? props.tab.iconClassActive ?? props.tab.iconClass
    : props.tab.iconClass;
});
</script>

<style lang="scss" scoped>
.nav-bar__item {
  --item-height: 40px;

  display: flex;
  align-items: center;
  flex: 1;
  padding: 8px 8px 8px 16px;
  color: var(--clr-n1);
  font-size: 0.9rem;
  letter-spacing: -0.2px;
  min-height: var(--item-height);
  max-height: var(--item-height);

  &:hover {
    cursor: pointer;
    background-color: var(--clr-p7);
    color: var(--clr-n1);
  }

  :first-child {
    margin-right: 8px;
  }

  &__text {
    width: 100%;
    font-size: 0.9rem;
    margin: 0;
    text-align: start;

    &:hover {
      text-decoration: underline;
    }
  }

  &.active {
    background-color: var(--clr-p8);
    color: var(--clr-n1);
  }
}

.nav-bar__item__collapse {
  @extend .nav-bar__item;
  z-index: 1; // prevent glitch animation when opening dropdown
}

.router-link-active > .nav-bar__item__text {
  font-weight: bold;
}
</style>
